// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {useContext, useEffect, useState, useMemo, useRef} from 'react';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import cn from 'classnames';
import styles from './blog.module.less';
import {Input} from '../../../../hola/pub/lib/input';
import {Article_card} from './article_card';
import Icon_chevron_left from '../../img/icons/24/chevron_left.svg';
import Icon_chevron_right from '../../img/icons/24/chevron_right.svg';
import Icon_search_line from '../../img/icons/24/search_line.svg';

interface i_article_data {
    url: string;
    category?: string | string[];
    title?: string;
    description?: string;
    img?: string;
    author?: string;
    min_read?: number;
}

const Categories_section = props=>{
    const {theme} = useContext(Settings_context);
    const {all_categories = [], active_category = 'All',
        set_active_category = ()=>null, search_query = '',
        set_search_query = ()=>null, title} = props;
    const categories_ref = useRef(null);
    const [scroll_position, set_scroll_position] = useState(0);
    const [max_scroll, set_max_scroll] = useState(0);
    useEffect(()=>{
        if (categories_ref.current)
        {
            const update_scroll_info = ()=>{
                const element = categories_ref.current;
                if (!element)
                    return;
                const current_scroll = element.scrollLeft;
                const max_scroll_possible =
                    element.scrollWidth - element.clientWidth;
                set_scroll_position(current_scroll);
                set_max_scroll(max_scroll_possible);
            };
            update_scroll_info();
            categories_ref.current.addEventListener('scroll',
                update_scroll_info);
            return ()=>{
                if (categories_ref.current)
                {
                    categories_ref.current.removeEventListener('scroll',
                        update_scroll_info);
                }
            };
        }
    }, [categories_ref.current]);
    const scroll_categories = direction=>{
        if (!categories_ref.current)
            return;
        const scroll_amount = 200;
        const current_scroll = categories_ref.current.scrollLeft;
        categories_ref.current.scrollTo({
            left: direction == 'right' ?
                current_scroll + scroll_amount :
                current_scroll - scroll_amount,
            behavior: 'smooth'
        });
    };
    return <div className={styles.categories_section}>
      <h5 className={cn(theme.H5, theme.bold_700, theme.color_n700)}>
        {title || (active_category !== 'All' ? active_category :
          'Explore categories')}
      </h5>
      <div className={styles.search_box}>
        <Input
          placeholder='Search Oculus blog'
          value={search_query}
          on_change={val=>set_search_query(val)}
          left_align_icon={true}
          Icon={Icon_search_line}
          icon_size='20'
          cl={styles.search_input_container}
          input_cls={styles.search_input_field}
        />
      </div>
      <div className={styles.categories_scroll_container}>
        <div className={styles.scroll_buttons}>
          <button
            className={cn(
              styles.scroll_button_prev,
              scroll_position <= 0 && styles.hidden
            )}
            onClick={()=>scroll_categories('left')}
          >
            <Icon_chevron_left width={16} height={16}/>
          </button>
          <button
            className={cn(
              styles.scroll_button_next,
              scroll_position >= max_scroll && styles.hidden
            )}
            onClick={()=>scroll_categories('right')}
          >
            <Icon_chevron_right width={16} height={16}/>
          </button>
        </div>
        <div className={styles.categories_list} ref={categories_ref}>
          <div
            className={cn(
              styles.category_item,
              active_category == 'All' && styles.category_item_selected
            )}
            onClick={()=>set_active_category('All')}
          >
            <p className={cn(theme.p14, theme.med)}>All</p>
          </div>
          {all_categories.map((category, index)=>
            <div
              key={index}
              className={cn(
                styles.category_item,
                active_category == category && styles.category_item_selected
              )}
              onClick={()=>set_active_category(category)}
            >
              <p className={cn(theme.p14, theme.med)}>{category}</p>
            </div>
          )}
        </div>
      </div>
    </div>;
};

const Pagination = props=>{
    const {theme} = useContext(Settings_context);
    const {current_page, total_pages, set_current_page} = props;
    if (total_pages <= 1)
        return null;
    const render_page_numbers = ()=>{
        const pages = [];
        const max_visible = 5;
        if (total_pages <= max_visible)
        {
            for (let i = 1; i <= total_pages; i++)
              pages.push(i);
        }
        else
        {
          if (current_page <= 3)
          {
              for (let i = 1; i <= 4; i++)
                pages.push(i);
              pages.push('...');
              pages.push(total_pages);
          }
          else if (current_page >= total_pages - 2)
          {
              pages.push(1);
              pages.push('...');
              for (let i = total_pages - 3; i <= total_pages; i++)
                pages.push(i);
          }
          else
          {
              pages.push(1);
              pages.push('...');
              pages.push(current_page - 1);
              pages.push(current_page);
              pages.push(current_page + 1);
              pages.push('...');
              pages.push(total_pages);
          }
        }
        return pages;
    };
    return <div className={styles.pagination_container}>
      <div
        className={cn(
          styles.pagination_arrow,
          current_page == 1 && styles.pagination_arrow_disabled
        )}
        onClick={()=>{
          if (current_page > 1)
            set_current_page(current_page - 1);
        }}
      >
        <div className={styles.arrow_left_icon}></div>
        <p className={cn(theme.p14, theme.med)}>Previous</p>
      </div>
      <div className={styles.pagination_numbers}>
        {render_page_numbers().map((page, index)=>
          typeof page == 'number' ?
            <div
              key={index}
              className={cn(
                styles.page_number,
                current_page == page && styles.page_number_selected
              )}
              onClick={()=>set_current_page(page)}
            >
              <p className={cn(theme.p14, theme.med)}>{page}</p>
            </div>
          :
            <div key={index} className={styles.page_ellipsis}>
              <p className={cn(theme.p14, theme.med)}>...</p>
            </div>
        )}
      </div>
      <div
        className={cn(
          styles.pagination_arrow,
          current_page == total_pages && styles.pagination_arrow_disabled
        )}
        onClick={()=>{
          if (current_page < total_pages)
            set_current_page(current_page + 1);
        }}
      >
        <p className={cn(theme.p14, theme.med)}>Next</p>
        <div className={styles.arrow_right_icon}></div>
      </div>
    </div>;
};

interface i_articles_section_props {
    articles?: i_article_data[];
    title?: string;
    initial_category?: string;
    category?: string;
}

export const Articles_section = (props: i_articles_section_props)=>{
    const {theme} = useContext(Settings_context);
    const {title: prop_title, initial_category,
        category: prop_category} = props;
    const [articles, set_articles] =
        useState<i_article_data[]>(props.articles);
    const [active_category, set_active_category] = useState(
        prop_category || initial_category || 'All'
    );
    const title_to_use = prop_title ||
        (active_category !== 'All' ? active_category : 'Explore categories');
    const [search_query, set_search_query] = useState('');
    const [current_page, set_current_page] = useState(1);
    const articles_per_page = 12;
    useEffect(()=>{
        if (props.articles && props.articles.length)
        {
          set_articles(props.articles);
          return;
        }
    }, [props.articles]);
    const find_original_category =
        (formatted: string, article_list: i_article_data[])=>{
        const all_cats = new Set<string>();
        article_list.forEach(article=>{
            if (article.category)
            {
                if (Array.isArray(article.category))
                    article.category.forEach(cat=>all_cats.add(cat.trim()));
                else
                    all_cats.add(article.category.trim());
            }
        });
        for (const cat of all_cats)
        {
            const cat_formatted = cat.replace(/\s+/g, '-').toLowerCase();
            if (cat_formatted == formatted)
              return cat;
        }
        for (const cat of all_cats)
        {
            const cat_formatted =
            cat.replace(/\s+/g, '-').toLowerCase().trim();
            if (cat_formatted == formatted.trim())
              return cat;
        }
        for (const cat of all_cats)
        {
            const cat_formatted =
                cat.replace(/\s+/g, '-').toLowerCase().trim();
            if (cat_formatted.toLowerCase() == formatted.toLowerCase().trim())
              return cat;
        }
        return null;
    };
    useEffect(()=>{
        const path_parts = window.location.pathname.split('/');
        let category_from_path = null;
        if (path_parts.includes('category') &&
            path_parts.length > path_parts.indexOf('category') + 1)
        {
            category_from_path =
                path_parts[path_parts.indexOf('category') + 1];
            if (category_from_path && articles && articles.length)
            {
                const found_category =
                    find_original_category(category_from_path, articles);
                if (found_category)
                  set_active_category(found_category);
            }
        }
    }, [articles, window.location.pathname]);
    const all_categories = useMemo(()=>{
        if (!articles || !articles.length)
            return [];
        const categories_set = new Set();
        articles.forEach(article=>{
            if (article.category)
            {
                if (Array.isArray(article.category))
                  article.category.forEach(cat=>categories_set.add(cat));
                else
                  categories_set.add(article.category);
            }
        });
        return Array.from(categories_set) as string[];
    }, [articles]);
    const filtered_articles = useMemo(()=>{
        if (!articles || !articles.length)
            return [];
        let all_articles = [...articles];
        if (active_category !== 'All')
        {
            all_articles = all_articles.filter(article=>{
              if (!article.category)
                  return false;
              if (Array.isArray(article.category))
                  return article.category.includes(active_category);
              return article.category == active_category;
          });
        }
        if (search_query)
        {
            const query = search_query.toLowerCase();
            all_articles = all_articles.filter(article=>{
                return article.title &&
                  article.title.toLowerCase().includes(query) ||
                  article.description &&
                  article.description.toLowerCase().includes(query) ||
                  article.author &&
                  article.author.toLowerCase().includes(query);
            });
        }
        return all_articles;
    }, [articles, active_category, search_query]);
    const paginated_articles = useMemo(()=>{
        if (!filtered_articles || !filtered_articles.length)
            return [];
        const start_index = (current_page - 1) * articles_per_page;
        return filtered_articles.slice(start_index,
            start_index + articles_per_page);
    }, [filtered_articles, current_page, articles_per_page]);
    const total_pages = useMemo(()=>{
        if (!filtered_articles || !filtered_articles.length)
            return 0;
        return Math.ceil(filtered_articles.length / articles_per_page);
    }, [filtered_articles, articles_per_page]);
    useEffect(()=>{
      set_current_page(1);
    }, [active_category, search_query]);
    return <div className={cn(theme.container, styles.articles_section)}>
      <Categories_section
        all_categories={all_categories}
        active_category={active_category}
        set_active_category={set_active_category}
        search_query={search_query}
        set_search_query={set_search_query}
        title={title_to_use}
      />
      <div className={styles.articles_grid}>
        {paginated_articles.map((article, index)=>
          <Article_card
            key={index}
            url={article.url || '#'}
            category={article.category}
            title={article.title || 'Sample Article'}
            description={article.description || 'Sample description'}
            img={article.img}
            author={article.author || 'Author'}
            min_read={article.min_read || 3}
            on_category_select={set_active_category}
          />
        )}
      </div>
      {filtered_articles.length > articles_per_page &&
        <Pagination
          current_page={current_page}
          total_pages={total_pages}
          set_current_page={set_current_page}
        />
      }
    </div>;
};
